import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions } from '@klv/hooks/useWindowDimensions';
import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import { generateClassName } from '@klv/shared/utils/functions';
import Button from '@klv/atoms/button';
import Icon from '@klv/atoms/icon';
import { FeaturesList } from '../features-list';
import * as styles from './styles/planCard.module.css';
import type { IPlanCard } from './types';

const PlanCard = ({
  buttonLink,
  buttonOpenInNewTab,
  buttonText = 'Sign up',
  contacts,
  description,
  featuresList,
  emailSends,
  id,
  name,
  price,
  credits,
  messagingType,
  setShowMaxContacts,
  isAllExpanded,
  ...props
}: IPlanCard) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const { width } = useWindowDimensions();
  const isMobile = width < 1024;

  const handleClick = () => {
    if (buttonText === (t('organisms_pricing_signUp') || 'Sign up')) {
      gtmBasicEvent({
        category: 'pricing card',
        action: 'signup cta',
        label: name.toLowerCase(),
      });
    }
    return true;
  };

  const handleContactSalesClick = () => setShowMaxContacts(true);

  const formattedPrice = price?.toLocaleString(locale, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

  const isBelow250Contacts = contacts <= 250;
  const isAbove250Contacts = contacts >= 250;
  const isAbove10kContacts = contacts >= 10000;
  const isAbove250kContacts = contacts >= 250001;


  const freeCardPlanNameAbove250k = name === t('organisms_pricing_plans_free_name') && isAbove250kContacts;
  const freeCardPlanNameBelow250 = name === t('organisms_pricing_plans_free_name') && isBelow250Contacts;

  const FreeCardContent = () => {
    if (name !== t('organisms_pricing_plans_free_name')) {
      return null;
    }
  
    if (isAbove250kContacts || isAbove250Contacts) {
      return (
        <div className={isAbove250kContacts ? styles.freeText : styles.start}>
          <div>{t('organisms_pricing_plans_free_subtext_top')}</div>
          <div className={styles.above250Subtext}>
            {t('organisms_pricing_plans_free_subtext_bottom')}
          </div>
        </div>
      );
    }
  
    if (isBelow250Contacts) {
      return (
        <div className={generateClassName([styles.freeCardStats])}>
          <div className={styles.freeCardEyebrow}>{'get started'}</div>
          <div aria-live="polite" className={styles.priceWrapper}>
            <div className={generateClassName([freeCardPlanNameBelow250 ? styles.freeAmount : styles.amount])}>{formattedPrice}</div>
            <div className={styles.timePeriod}>
              {t('organisms_pricing_per_month')}
            </div>
          </div>
          <>
            <div className={styles.emailSendsContainer}>
              <div className={generateClassName([styles.emailSendsText, styles.freeCardEmailSends])}>
                {emailSends.toLocaleString()} {t('organisms_pricing_plans_email_per_month')}
              </div>
            </div>
          </>
        </div>
      );
    }
  
    return null;
  };
  

  const PaidCardContent = ({ planType }: { planType: string; }) => {
    if (name !== planType) return null;

    if (isAbove250kContacts) {
      return (
        <div className={styles.text}>
          {t('organisms_pricing_plans_paidCardContent_above250kContacts')}
        </div>
      );
    }

    if (isBelow250Contacts) {
      return (
        <div className={styles.text}>
          {t('organisms_pricing_plans_paidCardContent_below250Contacts')}
        </div>
      );
    }

    if (
      isAbove250Contacts ||
      (planType === t('organisms_pricing_plans_emailPlusSms_name') &&
        isAbove10kContacts &&
        !isAbove250kContacts)
    ) {
      return (
        <>
          <div className={styles.amount}>{formattedPrice}</div>
          <div className={styles.timePeriod}>{t('organisms_pricing_per_month')}</div>
        </>
      );
    }

    return null;
  };

  const EmailCardContent = () => <PaidCardContent planType={t('organisms_pricing_plans_email_name')} />;
  const EmailPlusSmsContent = () => <PaidCardContent planType={t('organisms_pricing_plans_emailPlusSms_name')} />;

  const renderFeaturesList = (expandFlag: boolean) => {
    const itemsToDisplay = expandFlag ? featuresList : featuresList?.slice(0, 6);
    return itemsToDisplay?.map((item) => (
      <div className={styles.featuresList}>
        <FeaturesList key={item?.listText} listIcon={item?.listIcon} listText={item?.listText} />

      </div>
    ));
  };

  return (
    <div id={id} className={styles.planCard} {...props}>
      <div className={styles.cardContents}>
        <div className={generateClassName([styles.planNameContainer, freeCardPlanNameAbove250k ? styles.freeCardPlanNameContainer : ''])}>
          <div className={styles.planName}>{name}</div>
          {name === t('organisms_pricing_plans_emailPlusSms_name') && (
            <div className={styles.recommended}>
              <div className={styles.recommendedText}>
                {t('organisms_pricing_plans_emailPlusSms_recommended')}
              </div>
            </div>
          )}
        </div>

        {!isAbove250kContacts &&
          (name === 'Email' || name === t('organisms_pricing_plans_emailPlusSms_name')) &&
          isAbove250Contacts && (
            <div className={styles.priceEyebrow}>
              {t('organisms_pricing_startingAt')}
            </div>
        )}

        <div className={generateClassName([styles.statsContainer, freeCardPlanNameBelow250 ? styles.freeCardStatsContainer : ''])}>
          <div aria-live="polite" className={styles.priceWrapper}>
            <FreeCardContent />
            <EmailCardContent />
            <EmailPlusSmsContent />
          </div>
          {!isAbove250kContacts &&
            !isBelow250Contacts &&
            name !== t('organisms_pricing_plans_free_name') && (
              <>
                <div className={name === t('organisms_pricing_plans_email_name') ? styles.emailSendsContainer : styles.emailSendsPlusContainer}>
                  <div className={styles.emailSendsText}>
                  {emailSends.toLocaleString()} {t('organisms_pricing_plans_email_emails_label')}
                  {name === t('organisms_pricing_plans_emailPlusSms_name') ? <> +</> : <> {t('organisms_pricing_per_month')}</>}
                  </div>
                  <div className={styles.smsSubText}>
                    {credits?.toLocaleString()} {' '}
                    {t('organisms_pricing_plans_emailPlusSms_creditsSubText')}
                  </div>
                </div>
              </>
          )}
        </div>

        {<div className={styles.divider} />}
        {<div className={styles.description}>{description}</div>}
        {<div className={styles.divider} />}

        <div className={styles.buttonContainer}>
          {contacts < 10000 && (
            <Button
              className={styles.button}
              openInNewTab={buttonOpenInNewTab || undefined}
              text={buttonText}
              action={handleClick}
              url={buttonLink || undefined}
            />
          )}
          {isAbove10kContacts && !isAbove250kContacts && (
            <div className={styles.buttons}>
              <Button
                className={styles.button}
                openInNewTab={buttonOpenInNewTab || undefined}
                text={buttonText}
                action={handleClick}
                url={buttonLink || undefined}
              />
              <Button
                className={styles.button}
                text={t('organisms_navigation_dataActions_getADemo_button_label')}
                url={t('organisms_navigation_dataActions_getADemo_button_href')}
                variant="ghost"
                buttonColor="dark"
              />
            </div>
          )}
          {contacts > 250000 && (
            <Button
              className={styles.contactButton}
              openInNewTab={buttonOpenInNewTab || undefined}
              text={t('organisms_pricing_contactSales')}
              action={handleContactSalesClick}
              
            />
          )}
        </div>

        <>
        <div className={styles.featuresListContainer}>
          {isMobile ? renderFeaturesList(isExpanded) : renderFeaturesList(isAllExpanded)}

        </div>
          <button
            className={`${styles.collapsibleContainer} ${isMobile ? styles.mobile : styles.desktop}`}
            onClick={toggleExpanded}
          >
            {isExpanded ? (
              <div className={styles.collapsible}>
                <div>{t('organisms_pricing_plans_collapsible_button')}</div>
                <Icon className={styles.iconUp} iconName="ChevronDown" />
              </div>
            ) : (
              <div className={styles.collapsible}>
                <div>{t('organisms_pricing_plans_expandable_button')}</div>
                <Icon className={styles.iconDown} iconName="ChevronDown" />
              </div>
            )}
          </button>
        </>
      </div>
    </div>
  );
};

export default PlanCard;

